// Special keys for storing complex data as JSON strings
export const MEASURES_TO_ADDRESS_RISKS_KEY = '__measuresToAddressRisks__';
export const RISK_ASSESSMENT_AND_MITIGATION_MEASURES_KEY = '__riskAssessment__';

export const controllerAndAssessmentIdentificationFields = {
  title: 'Controller & Assessment Identification',
  fields: [
    {
      label: 'Organisation name',
      placeholder: 'Enter Name',
      key: 'organizationName'
    },
    {
      label: 'Address',
      placeholder: 'Enter Address',
      key: 'address'
    },
    {
      label: 'Contact person',
      placeholder: 'Enter contact person',
      key: 'contactPerson'
    },
    {
      label: 'Role/Title',
      placeholder: 'Enter role',
      key: 'role'
    },
    {
      type: 'email',
      label: 'Email',
      placeholder: 'Enter email',
      key: 'email'
    }
  ],
  dpo: {
    title: 'DPO / Data Protection Contact (if applicable)',
    fields: [
      {
        label: 'Contact person',
        placeholder: 'Enter contact person',
        key: 'dpoContactPerson'
      },
      {
        type: 'email',
        label: 'Email',
        placeholder: 'Enter email',
        key: 'dpoEmail'
      },
      {
        label: 'DPIA title',
        placeholder: 'Enter title',
        key: 'dpiaTitle'
      },
      {
        label: 'Reference ID',
        placeholder: 'Enter ID',
        key: 'referenceId'
      }
    ],
    groupfields: [
      {
        type: 'date',
        label: 'DPIA date',
        placeholder: 'Select date',
        key: 'dpiaDate'
      },
      {
        type: 'date',
        label: 'Date of Approval',
        placeholder: 'Select date',
        key: 'dateOfApproval'
      },
      {
        label: 'Prepared by (name, title)',
        placeholder: 'Enter details',
        key: 'preparedBy'
      },
      {
        label: 'Reviewed/approved by (name, title)',
        placeholder: 'Enter details',
        key: 'reviewedBy'
      }
    ]
  }
};

export const descriptionOfProcessingOperationsAndPurposesFields = {
  title: 'Description of Processing Operations & Purposes',
  fields: [
    {
      key: 'processingOverview',
      label: 'Processing overview',
      placeholder:
        'Describe the nature, scope, context and purposes of the processing operations. Include   information on systems involved (e.g. AI systems), business processes, and key stakeholders.'
    },
    {
      key: 'categoriesOfDataSubjects',
      label: 'Categories of data subjects',
      placeholder:
        'List the categories of individuals whose data will be processed (e.g. customers, employees, patients, users, citizens).'
    },
    {
      key: 'categoriesOfPersonalData',
      label: 'Categories of personal data',
      placeholder:
        'List categories of personal data, including any special categories of data (e.g. health, biometric, racial/ethnic origin, political opinions).'
    },
    {
      key: 'dataFlowsAndRecipients',
      label: 'Data flows & recipients',
      placeholder:
        'Summarise the main data flows, including sources, recipients, transfers to third parties and   any international transfers.'
    },
    {
      key: 'legalBasisForProcessing',
      label: 'Legal basis for processing',
      placeholder:
        'Specify the GDPR legal basis (e.g. consent, contract, legal obligation, vital interests, public task, legitimate interests) and, where relevant, the legal basis for special category data.'
    }
  ]
};

export const necessityAndProportionalityFields = {
  title: 'Necessity & Proportionality',
  fields: [
    {
      key: 'necessityOfProcessing',
      label: 'Necessity of processing',
      placeholder:
        'Explain why this processing is necessary to achieve the stated purposes. Consider whether less intrusive alternatives are possible.'
    },
    {
      key: 'dataMinimization',
      label: 'Data minimisation',
      placeholder:
        'Explain how the processing complies with the principle of data minimisation (only data that   is adequate, relevant, and limited to what is necessary is collected and used).'
    },
    {
      key: 'storageLimitation',
      label: 'Storage limitation',
      placeholder:
        'Describe retention periods and deletion/anonymisation schedules for the personal data.'
    },
    {
      key: 'transparencyAndInformationToDataSubjects',
      label: 'Transparency & information to data subjects',
      placeholder:
        'Describe how data subjects are informed about the processing (privacy notices, just-in-time notices, etc.).'
    },
    {
      key: 'dataSubjectsRights',
      label: 'Data subject rights',
      placeholder:
        'Describe how rights (access, rectification, erasure, restriction, portability, objection, rights related to automated decision-making) can be exercised in relation to this processing.'
    },
    {
      key: 'processorsAndJointControllers',
      label: 'Processors and joint controllers',
      placeholder:
        'List data processors and/or joint controllers involved, and briefly describe contractual   arrangements and responsibilities.'
    }
  ]
};

export const riskAssessmentAndMitigationMeasuresFields = {
  title: 'Risk Assessment – Rights & Freedoms of Data Subjects',
  description:
    'Identify and assess risks to the rights and freedoms of natural persons, taking into account likelihood and severity of impact. Consider risks such as unauthorised access or disclosure, data breaches, profiling and   discrimination, lack of transparency, inability to exercise rights, function creep, or misuse of data.',
  fields: [
    {
      type: 'text' as const,
      label: 'Risk ID',
      placeholder: 'Enter identifier',
      key: 'riskID'
    },
    {
      type: 'richtext' as const,
      label: 'Risk description',
      placeholder: 'Explain what could go wrong...',
      key: 'riskDescription'
    },
    {
      type: 'text' as const,
      label: 'Affected data subjects',
      placeholder: 'Enter details',
      key: 'affectedDataSubjects'
    },
    {
      type: 'radiobutton' as const,
      label: 'Likelihood',
      key: 'likelihoodOfOccurrence',
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' }
      ]
    },
    {
      type: 'radiobutton' as const,
      label: 'Severity',
      key: 'severity',
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' }
      ]
    },
    {
      type: 'radiobutton' as const,
      label: 'Initial Risk Level',
      key: 'initialRiskLevel',
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' }
      ]
    },
    {
      type: 'text' as const,
      label: 'Existing controls',
      placeholder: 'Enter details',
      key: 'existingControls'
    }
  ]
};

export const measuresToAddressRisksFields = {
  title: 'Measures to Address Risks',
  description:
    'Describe the measures envisaged to address the risks identified and demonstrate compliance with the GDPR (security, privacy by design and by default, policies, training, monitoring, etc.).',
  fields: [
    {
      label: 'Risk ID',
      placeholder: 'Enter identifier',
      type: 'text'
    },
    {
      label: 'Mitigation measure(s)',
      placeholder: 'Enter details',
      type: 'text'
    },
    {
      label: 'Type',
      placeholder: 'Explain type e.g technical/organisational...',
      type: 'text'
    },
    {
      label: 'Owner / responsible role',
      placeholder: 'Enter details',
      type: 'text'
    },
    {
      label: 'Implementation timeline',
      placeholder: 'Describe the timeline',
      type: 'text'
    },
    {
      label: 'Residual risk',
      placeholder: 'Select residual risk level',
      type: 'radiobutton',
      options: [
        { label: 'Low', value: 'low' },
        { label: 'Medium', value: 'medium' },
        { label: 'High', value: 'high' }
      ]
    }
  ]
};

export const consultationAndPriorConsultationFields = {
  title: 'Consultation & Prior Consultation',
  fields: [
    {
      key: 'internalConsultation',
      label: 'Internal consultation',
      placeholder:
        'Describe any internal consultations carried out (e.g. with business owners, security, legal,   ethics or risk teams) and summarise key outcomes.'
    },
    {
      key: 'dpoConsultation',
      label: 'DPO consultation',
      placeholder:
        'Summarise the advice of the Data Protection Officer (if appointed) and indicate whether   their advice has been followed. If not, explain why.'
    },
    {
      key: 'consultationWithDataSubjectsOrRepresentatives',
      label:
        'Consultation with data subjects or representatives (if applicable)',
      placeholder:
        'Describe any consultation with data subjects, their representatives or civil society   organisations, if carried out.'
    },
    {
      key: 'priorConsultationWithSupervisoryAuthority',
      label: 'Prior consultation with supervisory authority',
      placeholder:
        'Describe any consultation with data subjects, their representatives or civil society   organisations, if carried out.'
    }
  ]
};

export const finalConclusionsAndApprovalFields = {
  title: 'Final Conclusions & Approval',
  fields: [
    {
      key: 'overallAssessmentOfResidualRisk',
      label: 'Overall assessment of residual risk',
      type: 'radio' as const
    },
    {
      key: 'finalDecisionOnProcessing',
      label: 'Final decision on processing',
      type: 'radio' as const
    }
  ]
};

// Radio button options for overall assessment
export const overallAssessmentOptions = [
  {
    key: 'residualRiskAcceptable',
    label: 'Residual risks are acceptable in light of measures taken'
  },
  {
    key: 'residualRiskNonAcceptable',
    label: 'Residual risks are not acceptable – further mitigation required'
  }
];

// Radio button options for final decision
export const finalDecisionOptions = [
  {
    key: 'proceedWithProcessingAsPlanned',
    label: 'Proceed with processing as planned'
  },
  {
    key: 'proceedWithProcessingSubjectToConditions',
    label: 'Proceed with processing subject to conditions / improvements'
  },
  {
    key: 'postponeProcessing',
    label: 'Postpone or do not proceed with processing'
  }
];

// Keys for storing radio button state
export const DPIA_OVERALL_ASSESSMENT_KEY = '__overallAssessment__';
export const DPIA_FINAL_DECISION_KEY = '__finalDecision__';
